import { isRecord } from '@/utils/objects';
import type { AttributeNode, AttributeSource } from '../types';
import { firstAndFilter, readFilterFields, toString } from './parse-helpers';

// Attribute-like types from backend: object_attribute, event_attribute, etc.
// These all share the same { field, operator, value } shape.
const ATTRIBUTE_LIKE_TYPES = new Set([
  'attribute',
  'object_attribute',
  'event_attribute',
  'foreign_event_attribute',
  'inbound_attribute',
  'collection_attribute',
  'relationship_attribute',
]);

/**
 * The flat attribute-like types whose field is not read off the person, and
 * the attribute source each one names.
 */
const FLAT_ATTRIBUTE_SOURCES: ReadonlyMap<string, AttributeSource> = new Map([
  ['relationship_attribute', 'relationship'],
]);

/**
 * The leaf keys a campaign trigger tree nests its filter block under, and the
 * attribute source each one names.
 *
 * A trigger tree spells its leaves differently from the flat filter shapes
 * above: the block sits under a key naming what the field is read from, rather
 * than the fields sitting on the leaf itself. `attribute` reads the person, so
 * it carries no source; the other two belong to a relationship trigger and
 * mark which side of the relationship the field comes from.
 */
const NESTED_ATTRIBUTE_KEYS: ReadonlyArray<{ key: string; source?: AttributeSource }> = [
  { key: 'attribute' },
  { key: 'object_attribute', source: 'object' },
  { key: 'relationship_attribute', source: 'relationship' },
];

/**
 * The keys a backend filter block sits under, all carrying the same
 * `{ field, operator, value }` shape and differing only in what they are
 * about: the person's attribute, a related object's, or a relationship's.
 */
const FILTER_BLOCK_KEYS = ['attribute_filter', 'related_object_attr_filter', 'relationship_attr_filter'];

/** The first backend filter block on the node, read as an attribute. */
function parseFilterBlock(raw: unknown): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  for (const key of FILTER_BLOCK_KEYS) {
    const block = raw[key];
    if (!isRecord(block)) {
      continue;
    }
    const field = toString(block.attribute ?? block.field);
    return field == null ? null : { type: 'attribute', field, ...readFilterFields(block) };
  }
  return null;
}

/**
 * A saved filter referenced by id, as a placeholder row.
 *
 * The filter itself lives elsewhere and cannot be fetched from inside a
 * parser, so the row names the reference rather than what it holds.
 */
function parseSavedFilter(raw: unknown): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  const conditionId = raw.condition_id;
  if (typeof conditionId !== 'number' || conditionId <= 0) {
    return null;
  }
  if ('field' in raw || 'attribute' in raw || 'event' in raw || 'segment' in raw) {
    return null;
  }
  const inverse = raw.inverse === true;
  return {
    type: 'attribute',
    field: `saved filter #${conditionId}`,
    operator: inverse ? 'does not match' : 'matches',
    inverse,
  };
}

/** The operator label an email-validation condition reads as. */
function emailOperatorLabel(operator: string | undefined, inverse: boolean): string {
  if (operator === 'email_valid') {
    return inverse ? 'is not a valid email' : 'is a valid email';
  }
  if (operator === 'exists') {
    return inverse ? 'does not exist' : 'exists';
  }
  return inverse ? 'is not valid or does not exist' : 'is valid and exists';
}

/** An email-validation condition: `{ type: 'email', operator, inverse }`. */
function parseEmailValidation(raw: unknown): AttributeNode | null {
  if (!isRecord(raw) || raw.type !== 'email') {
    return null;
  }
  const inverse = raw.inverse === true;
  return {
    type: 'attribute',
    field: 'email',
    operator: emailOperatorLabel(toString(raw.operator), inverse),
    inverse,
  };
}

/** The newsletter a newsletter-recipient condition is about: its name, else its id, else a plain noun. */
function newsletterNameOf(raw: unknown): string {
  if (!isRecord(raw)) {
    return 'newsletter';
  }
  const block = isRecord(raw.newsletter_recipients) ? raw.newsletter_recipients : undefined;
  const newsletterName = toString(raw.newsletter_name ?? block?.name);
  if (newsletterName != null) {
    return newsletterName;
  }
  const newsletterId = toString(block?.id ?? raw.newsletter_id);
  return newsletterId != null ? `newsletter #${newsletterId}` : 'newsletter';
}

/** A newsletter-recipient condition, named by the newsletter it is about. */
function parseNewsletterRecipients(raw: unknown): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  if (raw.type !== 'newsletter_recipients' && !('newsletter_recipients' in raw)) {
    return null;
  }
  const inverse = raw.inverse === true || raw.negate === true;
  const name = newsletterNameOf(raw);
  return {
    type: 'attribute',
    field: `${inverse ? 'is not a recipient of' : 'is a recipient of'} ${name}`,
    inverse,
  };
}

/**
 * An attribute leaf whose filter block is nested under a key naming its
 * source, or `null` when the node carries no such block.
 *
 * Takes the node as `unknown` and narrows it here, so the one dictionary
 * contract in this module stays on the parser's own entry point.
 */
function parseNestedAttribute(raw: unknown): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  for (const { key, source } of NESTED_ATTRIBUTE_KEYS) {
    const block = raw[key];
    if (!isRecord(block)) {
      continue;
    }
    const field = toString(block.field ?? block.attribute);
    if (field == null) {
      continue;
    }
    const node: AttributeNode = { type: 'attribute', field, ...readFilterFields(block) };
    if (source != null) {
      node.source = source;
    }
    return node;
  }
  return null;
}

/**
 * Which attribute a workflow step's comparison reads its field from, by
 * `field_source`. A person attribute carries no source.
 */
const COMPARE_FIELD_SOURCES: ReadonlyMap<string, AttributeSource | null> = new Map([
  ['customer_attribute', null],
  ['event_attribute', 'event'],
  ['foreign_event_attribute', 'event'],
  ['journey_attribute', 'journey'],
]);

/** What a comparison's value names, by `value_source`. A literal has no label. */
const COMPARE_VALUE_SOURCE_LABELS: ReadonlyMap<string, string | null> = new Map([
  ['const', null],
  ['', null],
  ['customer_attribute', "the current person's attribute"],
  ['event_attribute', 'the trigger event attribute'],
  ['journey_attribute', 'the journey attribute'],
]);

/**
 * The flat comparison leaf a workflow step's conditions store:
 * `{ type: 'attribute_compare', field_source, field, operator, value, value_source }`.
 * Negation is a `!` on the operator, or an `inverse` flag. A field or value source this viewer has
 * no words for is left to the unknown row.
 */
function parseAttributeCompare(raw: unknown): AttributeNode | null {
  if (!isRecord(raw) || raw.type !== 'attribute_compare') {
    return null;
  }
  const field = toString(raw.field);
  const source = COMPARE_FIELD_SOURCES.get(toString(raw.field_source) ?? '');
  const valueSource = COMPARE_VALUE_SOURCE_LABELS.get(toString(raw.value_source) ?? '');
  if (field == null || source === undefined || valueSource === undefined) {
    return null;
  }
  const node: AttributeNode = { type: 'attribute', field, ...readFilterFields(raw) };
  // Negated by a `!` on the operator, or by an `inverse` flag beside a plain one.
  node.inverse = node.inverse || toString(raw.operator)?.startsWith('!') === true;
  if (source != null) {
    node.source = source;
  }
  if (valueSource != null) {
    node.valueSource = valueSource;
  }
  return node;
}

/**
 * Whether the node is a flat attribute leaf: `{ type: 'attribute', field,
 * operator, value }`, one of its attribute-like siblings, the backend's type 0,
 * or a bare `{ field, operator, value }`.
 */
function isFlatAttribute(raw: unknown): boolean {
  if (!isRecord(raw)) {
    return false;
  }
  const condType = raw.type;
  const hasField = 'field' in raw || 'attribute' in raw;
  return (
    (typeof condType === 'string' && ATTRIBUTE_LIKE_TYPES.has(condType)) ||
    condType === 0 ||
    condType === '0' ||
    (hasField && condType == null)
  );
}

/** A flat attribute leaf, marked with its source where its type names one. */
function parseFlatAttribute(raw: unknown): AttributeNode | null {
  if (!isRecord(raw)) {
    return null;
  }
  const field = toString(raw.field ?? raw.attribute ?? raw.name);
  if (field == null) {
    return null;
  }
  const node: AttributeNode = { type: 'attribute', field, ...readFilterFields(raw) };
  const source = typeof raw.type === 'string' ? FLAT_ATTRIBUTE_SOURCES.get(raw.type) : undefined;
  if (source != null) {
    node.source = source;
  }
  return node;
}

/**
 * Parse attribute conditions.
 * Handles: { attribute: { field, operator, value } } and its object and
 *            relationship siblings, as a trigger tree nests them
 *          { event: { type: 'attribute_change', ... } }
 *          { attribute_filter: { field, operator, value } }  (backend type: 0)
 *          { type: 'attribute', field, operator, value }
 *          { field, operator, value }  (bare filter)
 */
export function parseAttribute(raw: Record<string, unknown>): AttributeNode | null {
  const nested = parseNestedAttribute(raw);
  if (nested != null) {
    return nested;
  }

  // Ember format: { event: { type: 'attribute_change', name, filters: { and: [to, from] } } }
  const event = isRecord(raw.event) ? raw.event : undefined;
  if (event?.type === 'attribute_change') {
    const name = toString(event.name);
    if (name == null) return null;
    return {
      type: 'attribute',
      field: name,
      ...readFilterFields(firstAndFilter(event)),
    };
  }

  if (FILTER_BLOCK_KEYS.some((key) => isRecord(raw[key]))) {
    return parseFilterBlock(raw);
  }

  if (raw.type === 'attribute_compare') {
    return parseAttributeCompare(raw);
  }

  if (isFlatAttribute(raw)) {
    return parseFlatAttribute(raw);
  }

  return parseSavedFilter(raw) ?? parseEmailValidation(raw) ?? parseNewsletterRecipients(raw);
}
